import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";
import { INCOME_STATS, RECENT_JOBS, UPSKILL_SUGGESTIONS } from "@/mocks/jobs";

const NEARBY_JOBS = [
  { id: 1, title: "Crew Member",     type: "Landscaping",  pay: "$22/hr", distance: "1.2 mi", dot: "bg-green-500"  },
  { id: 2, title: "Moving Help",     type: "Labor",        pay: "$28/hr", distance: "0.8 mi", dot: "bg-red-500"    },
  { id: 3, title: "Office Cleaning", type: "Cleaning",     pay: "$18/hr", distance: "2.4 mi", dot: "bg-orange-500" },
  { id: 4, title: "Event Setup",     type: "Events",       pay: "$20/hr", distance: "3.1 mi", dot: "bg-orange-500" },
  { id: 5, title: "Site Laborer",    type: "Construction", pay: "$32/hr", distance: "4.5 mi", dot: "bg-sky-500"    },
];

const STAT_CARDS = [
  { label: "Total Earned",     value: INCOME_STATS.totalEarned,            icon: "ri-money-dollar-circle-line", color: "text-green-600",  bg: "bg-green-50"  },
  { label: "This Week",        value: INCOME_STATS.thisWeek,               icon: "ri-calendar-line",            color: "text-sky-600",    bg: "bg-sky-50"    },
  { label: "Active Jobs",      value: String(INCOME_STATS.activeJobs),     icon: "ri-briefcase-4-line",         color: "text-orange-600", bg: "bg-orange-50" },
  { label: "Pending Offers",   value: String(INCOME_STATS.pendingOffers),  icon: "ri-inbox-line",               color: "text-violet-600", bg: "bg-violet-50" },
  { label: "Your Rating",      value: `⭐ ${INCOME_STATS.rating}`,         icon: "ri-star-line",                color: "text-amber-600",  bg: "bg-amber-50"  },
  { label: "Jobs Completed",   value: String(INCOME_STATS.completedJobs),  icon: "ri-check-double-line",        color: "text-green-700",  bg: "bg-green-50"  },
];

export default function DashboardPage() {
  const [availableNow, setAvailableNow] = useState(false);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-28 pb-10 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-3">
              Income Dashboard
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
              Track Every Dollar.<br />
              <span className="text-emerald-500">Grow Every Day.</span>
            </h1>
            <p className="text-gray-500 text-base max-w-lg">
              Your personal income command center — live earnings, job history, upskill opportunities, and nearby gigs in one view.
            </p>
          </div>
          {/* Availability Toggle */}
          <div className="flex items-center gap-3 bg-gray-900 rounded-2xl px-5 py-4">
            <span className="text-sm text-gray-400 font-medium">Available Now</span>
            <button
              onClick={() => setAvailableNow(!availableNow)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 cursor-pointer ${availableNow ? "bg-emerald-500" : "bg-gray-600"}`}
              aria-label="Toggle availability"
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${availableNow ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
            {availableNow && (
              <span className="text-xs text-emerald-400 font-bold flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />Live
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Main Dashboard */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left: Stats + Recent Jobs */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {STAT_CARDS.map((s) => (
                <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.bg} mb-2`}>
                    <i className={`${s.icon} ${s.color} text-base`} />
                  </div>
                  <div className="text-xl font-extrabold text-gray-900">{s.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Earnings Chart Placeholder */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 text-sm">Earnings This Month</h3>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">+18% vs last month</span>
              </div>
              {/* Bar chart mock */}
              <div className="flex items-end gap-2 h-28">
                {[40, 65, 50, 80, 70, 90, 60, 75, 85, 95, 72, 88, 100, 78].map((h, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full rounded-t-md bg-emerald-500/20 hover:bg-emerald-500/40 transition-colors" style={{ height: `${h}%` }} />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span>Mar 1</span><span>Mar 14</span><span>Mar 31</span>
              </div>
            </div>

            {/* Recent Jobs Table */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-bold text-gray-900 text-sm">Recent Jobs</h3>
                <Link to="/find-work" className="text-xs text-emerald-600 font-semibold hover:underline">Find more jobs →</Link>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left text-xs font-bold text-gray-500 px-5 py-3">Job</th>
                    <th className="text-left text-xs font-bold text-gray-500 px-4 py-3 hidden sm:table-cell">Date</th>
                    <th className="text-left text-xs font-bold text-gray-500 px-4 py-3 hidden md:table-cell">Hours</th>
                    <th className="text-right text-xs font-bold text-gray-500 px-5 py-3">Earned</th>
                    <th className="text-right text-xs font-bold text-gray-500 px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {RECENT_JOBS.map((job, i) => (
                    <tr key={job.id} className={i < RECENT_JOBS.length - 1 ? "border-b border-gray-50" : ""}>
                      <td className="px-5 py-3 text-xs font-semibold text-gray-800">{job.title}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">{job.date}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">{job.hours}h</td>
                      <td className="px-5 py-3 text-xs font-bold text-green-700 text-right">{job.earned}</td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-xs bg-green-100 text-green-700 font-bold px-2 py-1 rounded-full">{job.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: Upskill + Nearby */}
          <div className="flex flex-col gap-6">
            {/* Upskill Engine */}
            <div className="bg-gradient-to-br from-emerald-50 to-sky-50 rounded-2xl border border-emerald-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-sparkling-2-line text-emerald-600 text-lg" />
                <h3 className="font-bold text-gray-900 text-sm">Upskill Engine</h3>
              </div>
              <p className="text-xs text-gray-500 mb-4">Certifications that unlock significantly higher pay:</p>
              <div className="flex flex-col gap-3">
                {UPSKILL_SUGGESTIONS.map((s) => (
                  <div key={s.title} className="bg-white rounded-xl border border-gray-100 p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <i className={`${s.icon} text-emerald-600 text-sm`} />
                      <span className="text-xs font-bold text-gray-800">{s.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 line-through">{s.currentPay}</span>
                      <i className="ri-arrow-right-line text-gray-400 text-xs" />
                      <span className="text-xs font-extrabold text-green-700">{s.newPay}</span>
                      <span className="ml-auto text-xs text-gray-400">{s.timeToComplete}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Nearby Jobs */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 text-sm">Near You</h3>
                <Link to="/find-work" className="text-xs text-emerald-600 font-semibold hover:underline">View all</Link>
              </div>
              <div className="flex flex-col gap-2">
                {NEARBY_JOBS.map((job) => (
                  <div key={job.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${job.dot}`} />
                      <div>
                        <div className="text-xs font-bold text-gray-800">{job.title}</div>
                        <div className="text-xs text-gray-400">{job.type} · {job.distance}</div>
                      </div>
                    </div>
                    <span className="text-xs font-extrabold text-gray-900">{job.pay}</span>
                  </div>
                ))}
              </div>
              <Link to="/find-work" className="mt-4 block w-full py-2.5 text-center text-xs font-bold text-emerald-600 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors">
                <i className="ri-search-line mr-1" />Browse All Jobs
              </Link>
            </div>

            {/* Quick actions */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h3 className="font-bold text-gray-900 text-sm mb-3">Quick Actions</h3>
              <div className="flex flex-col gap-2">
                {[
                  { label: "Update My Skills",    to: "/capabilities", icon: "ri-settings-3-line" },
                  { label: "Browse New Jobs",      to: "/find-work",    icon: "ri-briefcase-line"  },
                  { label: "View Pricing Plans",   to: "/pricing",      icon: "ri-price-tag-3-line" },
                ].map((a) => (
                  <Link key={a.label} to={a.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                    <i className={`${a.icon} text-gray-400`} />{a.label}
                    <i className="ri-arrow-right-s-line ml-auto text-gray-300" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
